import React, { useMemo, useState } from 'react';
import { Typography, Box, TextField, InputAdornment, Button } from '@mui/material';
import { Search } from '@mui/icons-material';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';

import PageTemplate from 'src/components/PageTemplate';
import { useExpertManagement } from 'src/hooks/useExpertManagement';
import { PATHS } from 'src/constants/config';

// Import our new components and hooks
import { useReportsData } from 'src/hooks/useReportsData';
import { useExternalEfforts } from 'src/hooks/useExternalEfforts';
import { useQueryFiltering } from 'src/hooks/useQueryFiltering';
import { calculateDateRanges, getExternalEffortsForCompany } from 'src/utils/reportUtils';
import { getCompanyEffortsData } from 'src/utils/effortsUtils';
import EffortsTable from './components/EffortsTable';
import DateRangePicker from './components/DateRangePicker';
import CompanyHeader from './components/CompanyHeader';
import ReportGenerationModal from './components/ReportGenerationModal';
import ExternalEffortModal from './components/ExternalEffortModal';

const Reports = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { experts, companiesWithActiveExperts } = useExpertManagement();

  // State management
  const [dateFilter, setDateFilter] = useState('currentMonth');
  const [customStartDate, setCustomStartDate] = useState(moment().startOf('month').format('YYYY-MM-DD'));
  const [customEndDate, setCustomEndDate] = useState(moment().endOf('month').format('YYYY-MM-DD'));
  const [searchQuery, setSearchQuery] = useState('');
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [companySpecificReportModal, setCompanySpecificReportModal] = useState({
    open: false,
    companyName: '',
  });

  // Calculate date ranges first (needed before useReportsData call)
  const dateRanges = useMemo(() => calculateDateRanges(customStartDate, customEndDate), [customStartDate, customEndDate]);

  // Get the active date range for the API
  const activeRange = dateRanges[dateFilter];
  const startDate = activeRange.start.format('YYYY-MM-DD');
  const endDate = activeRange.end.format('YYYY-MM-DD');

  // Custom hooks - pass dates to enable server-side filtering
  const { allQueries, allReports, isLoading, allReportsMutate, mutateAll } = useReportsData(startDate, endDate);

  const {
    externalEffortModalOpen,
    selectedCompanyForEffort,
    externalEffortForm,
    isSubmittingEffort,
    isEditMode,
    setExternalEffortModalOpen,
    handleOpenExternalEffortModal,
    handleOpenEditExternalEffortModal,
    handleSubmitExternalEffort,
    handleRemoveExternalEffort,
  } = useExternalEfforts();

  const { filteredQueriesByCompany } = useQueryFiltering(allQueries, experts, companiesWithActiveExperts, dateFilter, dateRanges, searchQuery);

  // Get companies with active experts for the modal
  const modalCompanies = useMemo(() => {
    if (!companiesWithActiveExperts) return [];

    return Object.entries(companiesWithActiveExperts)
      .filter(([, data]) => data.hasActiveExperts)
      .map(([company, data]) => ({
        name: company,
        experts: data.experts.filter((expert) => expert.isActive === 1),
      }));
  }, [companiesWithActiveExperts]);

  const handleViewAsExpert = (company) => {
    const formattedCompany = company.trim().toLowerCase().replaceAll(' ', '-');

    navigate(`${PATHS.REPORTS.PROFILE.value.replace(':companyID', formattedCompany)}/?startDate=${startDate}&endDate=${endDate}`);
  };

  const handleGenerateReport = (company) => {
    setCompanySpecificReportModal({
      open: true,
      companyName: company,
    });
  };

  const handleCloseCompanySpecificModal = () => {
    setCompanySpecificReportModal({
      open: false,
      companyName: '',
    });
  };

  const handleExternalEffortSubmit = (formData) => {
    handleSubmitExternalEffort(formData, allReports, allReportsMutate, dateRanges, dateFilter);
  };

  const handleExternalEffortRemove = (company, effortId) => {
    handleRemoveExternalEffort(company, effortId, allReports, allReportsMutate, dateRanges, dateFilter);
  };

  const handleExternalEffortEdit = (company, effort) => {
    handleOpenEditExternalEffortModal(company, effort);
  };

  const handleDateUpdate = () => {
    // Refresh all query data to reflect the updated reportDate
    mutateAll();
  };

  return (
    <PageTemplate loading={isLoading} loadingMessage="Loading takes about 15-30 seconds">
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { xs: 'stretch', md: 'center' },
            justifyContent: 'space-between',
            gap: 2,
            mb: 2,
          }}
        >
          <Typography
            variant="h4"
            sx={{
              flex: 1,
              fontWeight: 700,
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              color: 'transparent',
              letterSpacing: '-0.02em',
            }}
          >
            Reports
          </Typography>
          <TextField
            fullWidth
            placeholder="Search company or expert..."
            size="medium"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{
              flex: 3,
              boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'white',
              },
              '& .MuiOutlinedInput-root:hover': {
                backgroundColor: 'white',
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: { xs: 'center', md: 'flex-end' } }}>
            <DateRangePicker
              dateFilter={dateFilter}
              onDateFilterChange={setDateFilter}
              dateRanges={dateRanges}
              customStartDate={customStartDate}
              customEndDate={customEndDate}
              onCustomStartDateChange={setCustomStartDate}
              onCustomEndDateChange={setCustomEndDate}
            />
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flex: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Last sent: {moment().subtract(3, 'days').format('MMM DD, YYYY')}
          </Typography>
          <Button variant="contained" color="primary" onClick={() => setReportModalOpen(true)}>
            Generate & send reports
          </Button>
        </Box>

        {Object.entries(filteredQueriesByCompany)
          .sort(([companyA, dataA], [companyB, dataB]) => {
            // Put unassigned queries first
            if (dataA.isUnassigned && !dataB.isUnassigned) return -1;
            if (!dataA.isUnassigned && dataB.isUnassigned) return 1;
            // Then sort alphabetically
            return companyA.localeCompare(companyB);
          })
          .map(([company, data]) => {
            const companyData = companiesWithActiveExperts[company];

            // Skip company data check for unassigned queries
            if (!data.isUnassigned && !companyData?.hasActiveExperts) return null;

            const externalEfforts = getExternalEffortsForCompany(
              allReports,
              company,
              dateRanges[dateFilter].start.format('YYYY-MM-DD'),
              dateRanges[dateFilter].end.format('YYYY-MM-DD')
            );

            // Use utility function to get filtered data for both sections
            const { winsData, effortsData } = getCompanyEffortsData(data.queriesByExpert, externalEfforts);

            return (
              <Box
                key={company}
                sx={{
                  backgroundColor: 'white',
                  border: '1px solid #ddd',
                  p: { xs: 2, sm: 6 },
                  borderRadius: 1,
                }}
              >
                <CompanyHeader
                  company={company}
                  isUnassigned={data.isUnassigned}
                  searchQuery={searchQuery}
                  onOpenExternalEffortModal={handleOpenExternalEffortModal}
                  onViewAsExpert={handleViewAsExpert}
                  onGenerateReport={handleGenerateReport}
                />

                {/* Wins Table - Only show if there are wins */}
                {winsData.hasData && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 500, mb: 1, color: 'success.main' }}>
                      Wins (Successful)
                    </Typography>

                    <EffortsTable
                      filteredQueries={winsData.queries}
                      filteredExternalEfforts={winsData.externalEfforts}
                      onRemoveExternalEffort={(effortId) => handleExternalEffortRemove(company, effortId)}
                      onEditExternalEffort={handleExternalEffortEdit}
                      company={company}
                      statusType="wins"
                      showActions={true}
                      onDateUpdate={handleDateUpdate}
                      dateRanges={dateRanges}
                      dateFilter={dateFilter}
                      allReports={allReports}
                      allReportsMutate={allReportsMutate}
                    />
                  </Box>
                )}

                {/* PR Efforts Table - Only show if there are efforts */}
                {effortsData.hasData && (
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 500, mb: 1, color: 'primary.main' }}>
                      PR Efforts (In Progress)
                    </Typography>

                    <EffortsTable
                      filteredQueries={effortsData.queries}
                      filteredExternalEfforts={effortsData.externalEfforts}
                      onRemoveExternalEffort={(effortId) => handleExternalEffortRemove(company, effortId)}
                      onEditExternalEffort={handleExternalEffortEdit}
                      company={company}
                      statusType="inProgress"
                      showActions={true}
                      onDateUpdate={handleDateUpdate}
                      dateRanges={dateRanges}
                      dateFilter={dateFilter}
                      allReports={allReports}
                      allReportsMutate={allReportsMutate}
                    />
                  </Box>
                )}
              </Box>
            );
          })}
      </Box>

      {/* Report Generation Modal */}
      <ReportGenerationModal
        open={reportModalOpen}
        onClose={() => setReportModalOpen(false)}
        companies={modalCompanies}
        dateRange={dateRanges[dateFilter]}
        filteredQueriesByCompany={filteredQueriesByCompany}
      />

      {/* Company-specific Report Generation Modal */}
      <ReportGenerationModal
        open={companySpecificReportModal.open}
        onClose={handleCloseCompanySpecificModal}
        companies={modalCompanies}
        dateRange={dateRanges[dateFilter]}
        filteredQueriesByCompany={filteredQueriesByCompany}
        prefilledSearch={companySpecificReportModal.companyName}
        autoExpandedCompany={companySpecificReportModal.companyName}
      />

      {/* External Effort Modal */}
      <ExternalEffortModal
        open={externalEffortModalOpen}
        onClose={() => setExternalEffortModalOpen(false)}
        selectedCompany={selectedCompanyForEffort}
        formData={externalEffortForm}
        onSubmit={handleExternalEffortSubmit}
        isSubmitting={isSubmittingEffort}
        isEditMode={isEditMode}
      />
    </PageTemplate>
  );
};

export default Reports;
